import React, { useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import laser from "../../assets/homeAssets/laser.jpg";
import skincare from "../../assets/homeAssets/skincare.png";
import schulung from "../../assets/homeAssets/schulung.webp";

gsap.registerPlugin(ScrollTrigger);

// --- Styled Components ---

const PageWrapper = styled.div`
  width: 100%;
`;

const Spacer = styled.div`
  height: 0vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const Container = styled.section`
  height: 100vh;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const BackgroundImage = styled.div.attrs((props) => ({
  style: {
    backgroundImage: `url("${props.$img}")`,
  },
}))`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0;
  z-index: 1;
`;

const DarkOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle,
    rgba(0, 0, 0, 0.2) 0%,
    rgba(0, 0, 0, 0.6) 100%
  );
`;

const MainTitleWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 10rem;
  font-family: "The Seasons";
  font-size: 15vw;
  font-weight: 100;
  color: #8a7878;
  z-index: 10;
  width: 100%;
  justify-content: center;
  pointer-events: none;
  white-space: nowrap;
  @media (max-width: 800px) {
    gap: 20px;
  }
`;

const CardsLayer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
`;

const StyledServiceCard = styled.div`
  position: absolute;
  width: 50%;
  height: auto;
  min-height: 500px;
  max-width: 1000px;
  background-color: rgba(153, 147, 147, 0);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 40px;
  padding: 7em 5em;
  top: ${(props) => `calc(50% - 250px + ${props.$index * 35}px)`};
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.3);

  h3 {
    color: #fff;
    font-size: 5vw;
    margin-bottom: 1.5rem;
    margin-top: 0;
    font-family: "The Seasons", sans-serif;
    font-weight: 100;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }

  p {
    color: #fff; /* Changed to white for better readability against dark overlays */
    font-size: 20px;
    line-height: 1.8;
    font-weight: 300;
    opacity: 0.9;
  }

  @media (max-width: 1080px) {
    width: 85%;
  }
  @media (max-width: 580px) {
    padding: 3em;
    min-height: 400px;
    top: ${(props) => `calc(50% - 200px + ${props.$index * 20}px)`};
    h3 {
      font-size: 12vw;
    }
    p {
      font-size: 14px;
    }
  }
`;

// --- Updated German Data ---

const SERVICES = [
  {
    title: "Laser",
    desc: "Die dauerhafte Haarentfernung bietet eine effektive und sichere Lösung für glatte, haarfreie Haut. Bei Era Aesthetics & Academy arbeiten wir mit moderner Lasertechnologie, die für alle Hauttypen geeignet ist und langfristige Ergebnisse ermöglicht. Jede Behandlung wird individuell auf Haut- und Haartyp abgestimmt.",
    img: laser,
  },
  {
    title: "Facial",
    desc: "Gönn Dir eine professionelle Gesichtsbehandlung bei Era Aesthetics & Academy in München. Ob Tiefenreinigung, Anti-Aging oder Feuchtigkeitsboost – wir passen jede Behandlung an Deine Hautbedürfnisse an. Hochwertige Produkte und moderne Methoden sorgen für ein frisches, strahlendes Hautbild.",
    img: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?q=80&w=1748&auto=format&fit=crop",
  },
  {
    title: "Hautpflege",
    desc: "Verwöhne Deine Haut mit professioneller Hautpflege mit Wirkstoffkosmetik von NuSkin bei Era Aesthetics & Academy in München. Aktive Inhaltsstoffe wie Hyaluron, Vitamine und Peptide fördern die Regeneration Deiner Haut, verbessern sichtbar Struktur, Feuchtigkeit und Ausstrahlung – für ein gesundes, strahlendes Hautbild.",
    img: skincare,
  },
  {
    title: "Schulung",
    desc: "Die Era Aesthetics & Academy in München steht für exklusive 1:1-Schulungen auf höchstem Niveau. In unserem Academy-Bereich vermitteln wir fundiertes Fachwissen und praxisnahe Expertise in den Bereichen Microneedling, Aquafacial, Aquafacial in Kombination mit Microneedling sowie Dauerhafte Haarentfernung. Jede Schulung ist individuell konzipiert und erfüllt höchste Ansprüche an Qualität, Präzision und Professionalität.",
    img: schulung,
  },
];

// --- Component ---

const WeDoSection = () => {
  const container = useRef();
  const weRef = useRef();
  const doRef = useRef();

  useGSAP(
    () => {
      const cards = gsap.utils.toArray(".service-card");
      const bgs = gsap.utils.toArray(".bg-image");

      gsap.set(bgs, { scale: 1.1, opacity: 0 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top top",
          end: `+=${(cards.length + 1) * 100}%`,
          scrub: 1,
          pin: true,
        },
      });

      // Animation for "WIR TUN"
      tl.to(
        weRef.current,
        { xPercent: -150, opacity: 0, ease: "power2.inOut" },
        0,
      ).to(
        doRef.current,
        { xPercent: 150, opacity: 0, ease: "power2.inOut" },
        0,
      );

      cards.forEach((card, i) => {
        tl.from(
          card,
          {
            y: window.innerHeight,
            opacity: 0,
            scale: 0.8,
            duration: 1,
            ease: "power2.out",
          },
          `-=0.4`,
        );

        tl.to(
          bgs[i],
          {
            opacity: 1,
            scale: 1,
            duration: 1,
            ease: "power2.inOut",
          },
          "<",
        );

        if (i > 0) {
          tl.to(bgs[i - 1], { opacity: 0, duration: 0.5 }, "<");
        }
      });
    },
    { scope: container },
  );

  return (
    <PageWrapper>
      <Spacer />
      <Container ref={container}>
        {SERVICES.map((service, i) => (
          <BackgroundImage
            key={`bg-${i}`}
            className="bg-image"
            $img={service.img}
          >
            <DarkOverlay />
          </BackgroundImage>
        ))}

        <MainTitleWrapper>
          <span ref={weRef}>WE</span>
          <span ref={doRef}>DO</span>
        </MainTitleWrapper>

        <CardsLayer>
          {SERVICES.map((service, i) => (
            <StyledServiceCard key={i} className="service-card" $index={i}>
              <h3>{service.title}</h3>
              <p>{service.desc}</p>
            </StyledServiceCard>
          ))}
        </CardsLayer>
      </Container>
      <Spacer />
    </PageWrapper>
  );
};

export default WeDoSection;
